import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Link as MuiLink,
  TextField,
  Typography,
} from '@mui/material';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import IconAddress from '../../assets/ic-address.png';
import IconEmail from '../../assets/ic-email.png';
import IconPhone from '../../assets/ic-phone.png';
import CheckoutPageHeader from '../components/checkout/CheckoutPageHeader';
import PageContainer from '../layouts/PageContainer';
import { v2Colors } from '../design/theme';
import { submitCustomerEnquiry } from '../services/enquiry';
import { useV2Common } from '../state/CommonProvider';
import { SOCIAL_LINKS } from '../config/footerLinks';
import { paths } from '../config/paths';

const CONTACT_SOCIAL = [
  { Icon: FacebookIcon, ...SOCIAL_LINKS[0] },
  { Icon: InstagramIcon, ...SOCIAL_LINKS[1] },
  { Icon: LinkedInIcon, ...SOCIAL_LINKS[3] },
];

const formFieldSx = {
  '& .MuiOutlinedInput-root': {
    color: v2Colors.canvas,
    bgcolor: 'rgba(255,255,255,0.1)',
    borderRadius: 1.5,
    '& fieldset': { borderColor: 'rgba(248, 232, 204, 0.45)' },
    '&:hover fieldset': { borderColor: 'rgba(248, 232, 204, 0.75)' },
    '&.Mui-focused fieldset': { borderColor: v2Colors.canvas },
  },
  '& .MuiInputLabel-root': { color: 'rgba(248, 232, 204, 0.9)' },
  '& .MuiFormHelperText-root': { color: 'rgba(248, 232, 204, 0.8)' },
};

export default function ContactUs() {
  const { isDesktop } = useV2Common();
  const [submitting, setSubmitting] = useState(false);
  const [banner, setBanner] = useState(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const openUrl = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const onSubmit = async (data) => {
    setSubmitting(true);
    setBanner(null);
    try {
      await submitCustomerEnquiry(data);
      setBanner({ severity: 'success', message: 'Enquiry sent successfully!' });
      reset();
    } catch {
      setBanner({ severity: 'error', message: 'Failed to send query. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageContainer
      sx={{
        p: 0,
        width: '100%',
        maxWidth: { xs: '100%', lg: 1100 },
        px: { xs: 1.5, sm: 2 },
        pt: { xs: 1.5, sm: 2 },
        pb: 4,
      }}
    >
      <CheckoutPageHeader backTo={paths.home} title="Contact Us" compact />

      <Box
        sx={{
          display: 'flex',
          flexDirection: isDesktop ? 'row' : 'column',
          gap: { xs: 2, md: 3 },
          alignItems: 'stretch',
          width: '100%',
          minWidth: 0,
        }}
      >
        <Card
          elevation={0}
          sx={{
            flex: isDesktop ? '0 0 320px' : '1 1 auto',
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
            p: { xs: 2, sm: 2.5 },
            borderRadius: 2.5,
            border: `1px solid ${v2Colors.primary}`,
            bgcolor: v2Colors.surface,
            overflow: 'hidden',
          }}
        >
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2.5 }}>
            Contact Us
          </Typography>

          <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, opacity: 0.9, minWidth: 0 }}>
            <Box
              component="img"
              src={IconAddress}
              alt=""
              sx={{ width: 20, height: 20, mt: 0.25, flexShrink: 0 }}
            />
            <Typography variant="body2" sx={{ lineHeight: 1.6, wordBreak: 'break-word' }}>
            Country Chicken Pivate Limited: Ayyappa Society, Chanda Naik Nagar, Madhapur, Hyderabad, Telangana 500081
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, opacity: 0.9, minWidth: 0 }}>
            <Box component="img" src={IconEmail} alt="" sx={{ width: 20, height: 20, flexShrink: 0 }} />
            <Typography variant="body2" sx={{ lineHeight: 1.6, wordBreak: 'break-word' }}>
              customer.care@countrychickenco.in
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, mb: 3, opacity: 0.9 }}>
            <Box component="img" src={IconPhone} alt="" sx={{ width: 20, height: 20, flexShrink: 0 }} />
            <Typography variant="body2">+91 8096110961</Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {CONTACT_SOCIAL.map(({ Icon, label, url }) => (
              <Icon
                key={label}
                onClick={() => openUrl(url)}
                sx={{
                  color: v2Colors.primary,
                  fontSize: 36,
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.85 },
                }}
                aria-label={label}
              />
            ))}
          </Box>
        </Card>

        <Card
          elevation={0}
          sx={{
            flex: 1,
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
            p: { xs: 2, sm: 2.5, md: 3 },
            borderRadius: 2.5,
            bgcolor: v2Colors.primary,
            color: v2Colors.canvas,
            overflow: 'hidden',
          }}
        >
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2.5, color: 'inherit' }}>
            Send a message
          </Typography>

          {banner ? (
            <Alert severity={banner.severity} sx={{ mb: 2.5 }} onClose={() => setBanner(null)}>
              {banner.message}
            </Alert>
          ) : null}

          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{ width: '100%', minWidth: 0 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: isDesktop ? 'row' : 'column',
                gap: 2,
                mb: 2,
                width: '100%',
                minWidth: 0,
                '& > .MuiTextField-root': { flex: 1, minWidth: 0 },
              }}
            >
              <TextField
                fullWidth
                label="First Name"
                placeholder="Enter your first name"
                variant="outlined"
                sx={formFieldSx}
                {...register('firstName', { required: 'Required field' })}
                error={Boolean(errors.firstName)}
                helperText={errors.firstName?.message}
              />
              <TextField
                fullWidth
                label="Last Name"
                placeholder="Enter your last name"
                variant="outlined"
                sx={formFieldSx}
                {...register('lastName', { required: 'Required field' })}
                error={Boolean(errors.lastName)}
                helperText={errors.lastName?.message}
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: isDesktop ? 'row' : 'column',
                gap: 2,
                mb: 2,
                width: '100%',
                minWidth: 0,
                '& > .MuiTextField-root': { flex: 1, minWidth: 0 },
              }}
            >
              <TextField
                fullWidth
                label="Email Address"
                placeholder="Enter your email"
                variant="outlined"
                sx={formFieldSx}
                {...register('emailId', {
                  required: 'Required field',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
                error={Boolean(errors.emailId)}
                helperText={errors.emailId?.message}
              />
              <TextField
                fullWidth
                label="Mobile Number"
                placeholder="Enter your mobile number"
                variant="outlined"
                sx={formFieldSx}
                {...register('mobileNo', {
                  required: 'Required field',
                  pattern: {
                    value: /^[7896]\d{9}$/,
                    message: 'Invalid mobile number',
                  },
                })}
                error={Boolean(errors.mobileNo)}
                helperText={errors.mobileNo?.message}
              />
            </Box>

            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Write your message here"
              placeholder="Enter your message"
              variant="outlined"
              sx={{ ...formFieldSx, mb: 2.5, minWidth: 0 }}
              {...register('userMsg', { required: 'Required field' })}
              error={Boolean(errors.userMsg)}
              helperText={errors.userMsg?.message}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth={!isDesktop}
              disabled={submitting}
              sx={{
                bgcolor: v2Colors.canvas,
                color: v2Colors.primary,
                fontWeight: 700,
                borderRadius: 999,
                px: 4,
                py: 1.25,
                mb: 3,
                '&:hover': { bgcolor: v2Colors.white },
              }}
            >
              {submitting ? 'Sending…' : 'Send message'}
            </Button>
          </Box>

          <Typography
            variant="body2"
            sx={{ color: v2Colors.canvas, opacity: 0.9, lineHeight: 1.6, wordBreak: 'break-word' }}
          >
            Please visit our{' '}
            <MuiLink component={Link} to={paths.privacyPolicy} sx={{ color: 'inherit', fontWeight: 600 }}>
              privacy policy
            </MuiLink>{' '}
            page to know more about our policies.
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: v2Colors.canvas, opacity: 0.9, mt: 1.5, lineHeight: 1.6, wordBreak: 'break-word' }}
          >
            To know about our terms and conditions, please visit our{' '}
            <MuiLink component={Link} to={paths.termsAndConditions} sx={{ color: 'inherit', fontWeight: 600 }}>
              terms and conditions
            </MuiLink>{' '}
            page.
          </Typography>
        </Card>
      </Box>
    </PageContainer>
  );
}
